import { useCallback, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ChatDocumentDTO } from '@shared/schemas/chat';
import {
  Artifact,
  ArtifactClose,
  ArtifactContent,
  ArtifactHeader,
  ArtifactTitle,
} from '@/components/ai-elements/artifact';
import { getChatErrorMessage } from '../chat-errors';
import { parseCodegenGenerationId } from '../codegen-document';
import { documentGenerationQueryKey, useDocumentGenerationStatus } from '../hooks/useDocumentGeneration';
import { getDocumentGeneration } from '../services/document-generation.api';
import { CanvasLoadError, CanvasPlaceholder } from './CanvasPlaceholder';
import { DocumentCanvas } from './DocumentCanvas';

type GeneratedDocumentPanelProps = {
  conversationId: string;
  document: ChatDocumentDTO;
  onClose: () => void;
  /**
   * Panneau historique d'un document-fichier (voie script), alimenté par le fichier résolu.
   * `refreshSource` renvoie une URL de téléchargement neuve (les URL présignées expirent).
   */
  renderFileDocument: (document: ChatDocumentDTO, refreshSource: () => Promise<string | null>) => ReactNode;
};

/**
 * Aiguillage d'un document de génération ouvert sans fichier : le statut dit s'il s'agit
 * d'un document par blocs (canvas) ou d'un fichier de la voie script (panneau historique).
 */
export function GeneratedDocumentPanel({
  conversationId,
  document,
  onClose,
  renderFileDocument,
}: GeneratedDocumentPanelProps) {
  const generationId = parseCodegenGenerationId(document) ?? '';
  const statusQuery = useDocumentGenerationStatus(conversationId, generationId);
  const queryClient = useQueryClient();
  const status = statusQuery.data;
  const refreshSource = useCallback(async () => {
    const fresh = await getDocumentGeneration(conversationId, generationId);
    queryClient.setQueryData(documentGenerationQueryKey(conversationId, generationId), fresh);
    return fresh.status === 'succeeded' ? fresh.result.downloadUrl : null;
  }, [conversationId, generationId, queryClient]);
  const fileDocument = useMemo<ChatDocumentDTO | null>(() => (
    status?.status === 'succeeded' && status.engine !== 'blocks'
      ? {
        ...document,
        filename: status.result.filename,
        mediaType: status.result.contentType,
        sourceUrl: status.result.downloadUrl,
      }
      : null
  ), [document, status]);

  if (status?.engine === 'blocks') {
    return <DocumentCanvas conversationId={conversationId} document={document} onClose={onClose} />;
  }
  if (fileDocument) {
    return renderFileDocument(fileDocument, refreshSource);
  }
  if (!status && document.sourceUrl) {
    // Fichier déjà résolu par l'appelant : on l'affiche sans attendre le statut.
    return renderFileDocument(document, refreshSource);
  }

  const failureMessage = status?.status === 'failed' || status?.status === 'dead'
    ? getChatErrorMessage({ code: status.errorCode })
    : statusQuery.isError ? getChatErrorMessage(statusQuery.error) : null;

  return (
    <Artifact className="h-full rounded-none border-none bg-sidebar shadow-none">
      <ArtifactHeader className="h-[3.25rem] border-border/50 bg-muted/20 px-4">
        <ArtifactTitle className="truncate text-sm font-semibold">{document.title}</ArtifactTitle>
        <ArtifactClose aria-label="Fermer le panneau" onClick={onClose} />
      </ArtifactHeader>
      <ArtifactContent className="flex-1 overflow-hidden p-0">
        {failureMessage
          ? <CanvasLoadError message={failureMessage} onRetry={() => void statusQuery.restartPolling()} />
          : <CanvasPlaceholder label={status ? 'Génération en cours…' : 'Chargement du document…'} />}
      </ArtifactContent>
    </Artifact>
  );
}
